import * as Sentry from '@sentry/capacitor'
import { ApolloClient, InMemoryCache, HttpLink, ApolloLink } from '@apollo/client'
import { Observable } from '@apollo/client/utilities'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import config from '../config'

export const authLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    (async () => {
      try {
        // Get the auth0 instance from window if available
        // this has been set from auth0-provider-with-navigate on react app load, so it should be available now (per request)
        const auth0 = window.__auth0_instance__

        if (!auth0) {
          console.warn('Auth0 instance not available in apollo authLink')
        } else {
          try {
            const token = await auth0.getAccessTokenSilently({
              cacheMode: 'on',
            })

            if (!token) {
              console.error('getAccessTokenSilently returned empty token')
            } else {
              operation.setContext(({ headers = {} }) => ({
                headers: {
                  ...headers,
                  authorization: `Bearer ${token}`,
                }
              }))
            }
          } catch (error) {
            console.error('Error getting access token:', error)
            console.error('Error details:', {
              message: error instanceof Error ? error.message : 'Unknown error',
              name: error instanceof Error ? error.name : 'Unknown',
              stack: error instanceof Error ? error.stack : undefined
            })
          }
        }

        // Subscribe to the next link
        const subscription = forward(operation).subscribe({
          next: (result) => observer.next(result),
          error: (error) => observer.error(error),
          complete: () => observer.complete(),
        })

        // Return cleanup function
        return () => subscription.unsubscribe()
      } catch (error) {
        observer.error(error)
      }
    })()
  })
})

const errorLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (result) => {
        // Handle GraphQL errors
        if (result.errors) {
          result.errors.forEach((error) => {
            console.error(
              `[GraphQL error]: Message: ${error.message}, Location: ${JSON.stringify(error.locations)}, Path: ${error.path}`,
              error.extensions
            )
            Sentry.captureMessage(`GraphQL error: ${error.message}`, { extra: { path: error.path } })
          })
        }
        observer.next(result)
      },
      error: (networkError) => {
        console.error('[Network error]:', networkError)
        observer.error(networkError)
      },
      complete: () => observer.complete(),
    })

    return () => subscription.unsubscribe()
  })
})

// Custom error type for network errors with status codes
interface NetworkErrorWithStatus extends Error {
  statusCode?: number
}

// Network error handler
const handleNetworkError = (error: NetworkErrorWithStatus) => {
  console.error('[Network error]:', error)

  // Check if it's a 401 Unauthorized error
  if (error.statusCode === 401) {
    console.error('Received 401 Unauthorized from server')

    // Try to refresh the token first
    const auth0 = window.__auth0_instance__
    if (auth0) {
      console.warn('Attempting to refresh access token...')

      auth0.getAccessTokenSilently({
        cacheMode: 'off', // Force token refresh
      })
        .then((token) => {
          if (token) {
            console.warn('Token refreshed successfully, reloading page')
            // Token refreshed successfully, reload to retry
            window.location.reload()
          } else {
            console.error('Token refresh returned empty token, logging out')
            logoutUser()
          }
        })
        .catch((refreshError) => {
          console.error('Failed to refresh token:', refreshError)
          // Token refresh failed, log out the user
          logoutUser()
        })
    } else {
      console.error('Auth0 instance not available, redirecting to home')
      window.location.href = '/'
    }
  }
}

const logoutUser = async () => {
  const auth0 = window.__auth0_instance__
  const isMobile = Capacitor.isNativePlatform()

  // Use custom URL scheme for mobile apps, regular URL for web
  const logoutUri = isMobile
    ? 'com.futyclub.app://landing'
    : window.location.origin

  if (auth0) {
    console.warn('Logging out user and redirecting to home')

    if (isMobile) {
      // On mobile, use Auth0's logout with Browser.open
      try {
        await auth0.logout({
          logoutParams: {
            returnTo: logoutUri,
          },
          async openUrl(url: string) {
            // Redirect using Capacitor's Browser plugin
            await Browser.open({
              url,
              windowName: "_self"
            })
          }
        })
      } catch (error) {
        console.error('[Apollo Logout] Error during logout:', error)
        // Fallback to manual redirect on error
        window.location.href = '/'
      }
    } else {
      // On web, do full logout with redirect to Auth0
      auth0.logout({
        logoutParams: {
          returnTo: logoutUri
        }
      })
    }
  } else {
    window.location.href = '/'
  }
}

// Override fetch to handle network errors
const fetchWithErrorHandling = (uri: RequestInfo | URL, options?: RequestInit) => {
  return fetch(uri, options).then((response) => {
    if (response.status === 401) {
      // Create a custom error for 401 responses
      const error: NetworkErrorWithStatus = new Error('Unauthorized')
      error.statusCode = 401
      handleNetworkError(error)
      // Still return the response so Apollo can process it
      return response
    }
    return response
  }).catch((error) => {
    console.error('Fetch error:', error)
    throw error
  })
}

const httpLinkWithErrorHandling = new HttpLink({
  uri: `${config.api.serverUrl}/graphql`,
  fetch: fetchWithErrorHandling,
})

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLinkWithErrorHandling]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
  },
})
